import { observable } from '@legendapp/state';
import { syncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { createSyncCreate, createSyncUpdate, defaultSyncConfig } from './syncHelpers';
import { createNewCard, getDueCards } from '../utils/fsrs';
import { getDeckById } from './decks$';
import {
  trackEvent,
  setOnceUserProperties,
  incrementUserProperties,
} from '../lib/analytics';

// Cards store with Supabase sync (local-first)
// NOTE: We explicitly filter out deleted records at query level to prevent ghost cards
// This works together with fieldDeleted in defaultSyncConfig for proper soft delete handling
export const cards$ = observable(syncedSupabase({
  supabase,
  collection: 'reedle_cards',
  // Explicitly exclude deleted records - belt-and-suspenders with fieldDeleted
  filter: (select) => select.or('deleted.is.null,deleted.eq.false'),
  create: createSyncCreate('reedle_cards'),
  update: createSyncUpdate('reedle_cards'),
  ...defaultSyncConfig,
  persist: {
    name: 'reedle-cards',
    plugin: ObservablePersistLocalStorage,
    retrySync: true,
  },
}));

/**
 * Get all cards as array
 */
export const getCardsArray = () => {
  const cardsObj = cards$.get() || {};
  return Object.values(cardsObj)
    .filter(card => card && card.id && !card.deleted)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
};

/**
 * Get cards for a specific deck
 */
export const getCardsByDeckId = (deckId) => {
  return getCardsArray().filter(card => card.deck_id === deckId);
};

/**
 * Get card by ID
 */
export const getCardById = (cardId) => {
  return cards$[cardId].get();
};

/**
 * How many cards this deck will actually hand you today.
 *
 * Not the raw count of cards past their due date: the deck's own daily limits
 * cap what a session serves, so a badge that ignored them would promise more
 * than Review delivers. Every surface that offers to start a review — the
 * sidebar badge, the Decks index, the deck screen — asks this one question.
 */
export const getDueCountForDeck = (deckId) => getDeckDueCards(deckId).length;

/** The cards a review session would serve for this deck, in session order. */
export const getDeckDueCards = (deckId) => {
  const deck = getDeckById(deckId);
  return getDueCards(getCardsByDeckId(deckId), {
    newCardsPerDay: deck?.new_cards_per_day || 20,
    reviewCardsPerDay: deck?.review_cards_per_day || 200,
  });
};

/**
 * Create a new card
 */
export const createCard = async ({ deckId, front, back, sourceArticleId, sourceMessageIndex }) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  const id = uuidv4();
  const now = new Date().toISOString();
  const fsrsFields = createNewCard();

  const newCard = {
    id,
    deck_id: deckId,
    user_id: userId,
    front: front.trim(),
    back: back.trim(),
    // FSRS fields
    ...fsrsFields,
    // Source tracking
    source_article_id: sourceArticleId || null,
    source_message_index: sourceMessageIndex ?? null,
    created_at: now,
    updated_at: now,
  };

  cards$[id].set(newCard);

  // Analytics
  const cardProps = {
    card_id: id,
    source: sourceMessageIndex != null ? 'ai_chat' : 'manual',
    deck_id: deckId,
    article_id: sourceArticleId || null,
    is_readonly: false,
  };
  trackEvent('card_created', cardProps);
  setOnceUserProperties({ first_card_created_at: new Date().toISOString() });
  trackEvent('first_card_created', cardProps);
  incrementUserProperties({ total_cards_created: 1 });

  return newCard;
};

/**
 * Create a card from a comment (for system Comments deck)
 * These cards are readonly and linked to their source comment/highlight
 */
export const createCommentCard = ({ deckId, front, back, sourceArticleId, sourceCommentId, sourceHighlightId }) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  const id = uuidv4();
  const now = new Date().toISOString();
  const fsrsFields = createNewCard();

  const newCard = {
    id,
    deck_id: deckId,
    user_id: userId,
    front: front.trim(),
    back: back.trim(),
    // FSRS fields
    ...fsrsFields,
    // Source tracking
    source_article_id: sourceArticleId || null,
    source_comment_id: sourceCommentId || null,
    source_highlight_id: sourceHighlightId || null,
    is_readonly: true,
    created_at: now,
    updated_at: now,
  };

  cards$[id].set(newCard);

  // Analytics
  const source = sourceHighlightId ? 'highlight' : 'comment';
  const cardProps = {
    card_id: id,
    source,
    deck_id: deckId,
    article_id: sourceArticleId || null,
    is_readonly: true,
  };
  trackEvent('card_created', cardProps);
  setOnceUserProperties({ first_card_created_at: new Date().toISOString() });
  trackEvent('first_card_created', cardProps);
  incrementUserProperties({ total_cards_created: 1 });

  return newCard;
};

/**
 * Check if a card is readonly (e.g., system-generated from comments)
 */
export const isCardReadonly = (cardId) => {
  const card = cards$[cardId]?.get();
  return card?.is_readonly === true;
};

/**
 * Get card by source comment ID
 */
export const getCardByCommentId = (commentId) => {
  const cardsObj = cards$.get() || {};
  return Object.values(cardsObj).find(
    card => card && card.source_comment_id === commentId && !card.deleted
  );
};

/**
 * Get card by source article ID and message index (for AI chat cards)
 */
export const getCardBySourceMessage = (articleId, messageIndex) => {
  if (messageIndex === undefined || messageIndex === null) return null;

  const cardsObj = cards$.get() || {};
  return Object.values(cardsObj).find(
    card => card &&
      card.source_article_id === articleId &&
      card.source_message_index === messageIndex &&
      !card.deleted
  ) || null;
};

/**
 * Get card by source article ID, message index, AND front text.
 * Used for per-card duplicate detection when multiple cards come from the same message.
 */
export const getCardBySourceMessageAndFront = (articleId, messageIndex, front) => {
  if (messageIndex === undefined || messageIndex === null || !front) return null;

  const normalizedFront = front.toLowerCase().trim();
  const cardsObj = cards$.get() || {};
  return Object.values(cardsObj).find(
    card => card &&
      card.source_article_id === articleId &&
      card.source_message_index === messageIndex &&
      card.front?.toLowerCase().trim() === normalizedFront &&
      !card.deleted
  ) || null;
};

/**
 * Delete cards by comment ID (for cascade delete when comment is deleted)
 */
export const deleteCardsByCommentId = (commentId) => {
  const cardsObj = cards$.get() || {};
  Object.values(cardsObj).forEach(card => {
    if (card && card.source_comment_id === commentId && !card.deleted) {
      cards$[card.id].delete();
    }
  });
};

/**
 * Update a card (for editing front/back)
 */
export const updateCard = (cardId, updates) => {
  cards$[cardId].assign({
    ...updates,
    updated_at: new Date().toISOString(),
  });
};

/**
 * Move card to a different deck
 */
export const moveCardToDeck = (cardId, deckId) => {
  cards$[cardId].assign({
    deck_id: deckId,
    updated_at: new Date().toISOString(),
  });
};

/**
 * Update card after review (FSRS scheduling)
 */
export const updateCardScheduling = (cardId, schedulingData) => {
  cards$[cardId].assign({
    due: schedulingData.due,
    stability: schedulingData.stability,
    difficulty: schedulingData.difficulty,
    elapsed_days: schedulingData.elapsed_days,
    scheduled_days: schedulingData.scheduled_days,
    reps: schedulingData.reps,
    lapses: schedulingData.lapses,
    state: schedulingData.state,
    last_review: schedulingData.last_review,
    updated_at: new Date().toISOString(),
  });
};

/**
 * Delete a card
 */
export const deleteCard = (cardId) => {
  cards$[cardId].delete();
};

